import React, { useContext, useEffect, useState } from 'react';
import { Button, DatePicker, Flex, Form, Input, Popover, Select, Switch, message } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, CheckOutlined, QuestionCircleOutlined, FieldTimeOutlined, GithubOutlined, VideoCameraAddOutlined, CreditCardOutlined, MailOutlined, ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { FormCard, FormSectionTitle, FormContainer, EmailList } from '@leedpulse-monorepo/components';
import { countryCodes, cleanPhoneNumber, isValidPhoneNumber } from '@leedpulse-monorepo/utils';
import {
  UserRole,
  userRoleAsString,
  UserType,
  userTypeAsString,
  ContextStatus,
  ECreditType,
  creditTypeAsString,
  EChannel,
  EAIReminderTiming,
  aiReminderTimingAsString,
} from '@leedpulse-monorepo/enums';
import UserCreationFormContext from '../../contexts/forms/users/UserCreationFormContext';
import PlansContext from '../../contexts/data/PlansContext';
import TextArea from 'antd/es/input/TextArea';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import SubscriptionTimezoneCity, {
  subscriptionTimezoneCityAsString,
} from '../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCity';
import SubscriptionTimezoneContinent, {
  subscriptionTimezoneContinentAsString,
} from '../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneContinent';
import SubscriptionTimezoneCountry, {
  subscriptionTimezoneCountryAsString,
} from '../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCountry';
import AIGender, { aiGenderAsString } from '../../../domain/enums/ai/AIGender';
import dayjs from 'dayjs';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import AvatarInput from '../@shared/AvatarInput';
import NumbersContext from '../../contexts/data/NumbersContext';
import DomainsContext from '../../contexts/data/DomainsContext';
import DomainsSelect from '../domains_page/select/DomainsSelect';
import AITon, { aiTonAsString } from '../../../domain/enums/ai/AITon';
import UsersContext from '../../contexts/data/UsersContext';
import SubscriptionDataSource, { subscriptionDataSourceAsString } from '../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import { handleErrorInputMessage, handleInputError } from '../../../core/utils/errorUtils';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const UserCreationForm: React.FC = () => {
  const navigate = useNavigate();

  const formContext = useContext(UserCreationFormContext);
  const plansContext = useContext(PlansContext);
  const authContext = useContext(AuthenticationContext);
  const numbersContext = useContext(NumbersContext);
  const usersContext = useContext(UsersContext);
  const domainsContext = useContext(DomainsContext);

  // State
  const [typeCreation, setTypeCreation] = useState<UserType | undefined>(
    undefined
  );

  useEffect(() => {
    // Get URL parameter type
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type');
    if (type !== null) {
      // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
      // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
      // external system") ; le setState synchrone est le reset qui precede l'appel.
      // eslint-disable-next-line react/set-state-in-effect
      setTypeCreation(type as UserType);
      formContext.onUserTypeChange(type as UserType);
    }

    // Get URL parameter subscriptionId
    const subscriptionId = urlParams.get('subscriptionId');
    if (subscriptionId !== null) {
      formContext.onLinkedSubscriptionIdChange(subscriptionId);
    }

    // Get URL parameter companyId
    const companyId = urlParams.get('companyId');
    if (companyId !== null) {
      formContext.onLinkedCompanyIdChange(companyId);
    }

    // If parameters are not settled, redirect to home
    if (type === null && subscriptionId === null) {
      navigate('/');
    }
    if (
      type?.includes(UserType.USER) &&
      subscriptionId === null &&
      companyId === null
    ) {
      navigate('/');
    }

    // Set default values if Leedpulse employee role is not highest
    if (
      authContext.authentication?.role !== UserRole.owner &&
      authContext.authentication?.role !== UserRole.admin
    ) {
      formContext.onUserRoleChange(UserRole.customer);
    }
  }, []);

  useEffect(() => {
    if (numbersContext.status === ContextStatus.IDLE) {
      numbersContext.fetch({
        available: true,
        channel: EChannel.sms
      });
    }
  }, [numbersContext.status]);

  useEffect(() => {
    switch (formContext.status) {
      case ContextStatus.SUCCESS:
        message.success('Nouveau compte créé avec succès');
        navigate(SUBSCRIPTIONS_PAGE_PATH);
        break;
      case ContextStatus.ERROR:
        message.error(
          formContext.errorMessage
        );
        break;
      default:
        break;
    }
  }, [formContext.status]);

  useEffect(() => {
    usersContext.fetchRoles([UserRole.maintainer, UserRole.admin, UserRole.owner]);
  }, []);

  const generatePassword = () => {
    // Generate a 10 characters password with at least one lowercase, one uppercase, one digit and one special character
    const lowercase = 'abcdefghkmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '23456789';
    const special = '!@#&+?';

    // Generate it
    let password = '';
    for (let i = 0; i < 14; i++) {
      const type = Math.floor(Math.random() * 4);
      switch (type) {
        case 0:
          password += lowercase[Math.floor(Math.random() * lowercase.length)];
          break;
        case 1:
          password += uppercase[Math.floor(Math.random() * uppercase.length)];
          break;
        case 2:
          password += digits[Math.floor(Math.random() * digits.length)];
          break;
        case 3:
          password += special[Math.floor(Math.random() * special.length)];
          break;
      }
    }

    formContext.onUserPasswordChange(password);
  };

  const getAvailableRoles = () => {
    if (authContext.authentication === null) {
      return [];
    }
    switch (authContext.authentication.role) {
      case UserRole.owner:
        return [
          UserRole.owner,
          UserRole.admin,
          UserRole.maintainer,
          UserRole.guest,
          UserRole.customer,
        ];
      case UserRole.admin:
        return [UserRole.maintainer, UserRole.guest, UserRole.customer];
      case UserRole.maintainer:
        return [UserRole.guest, UserRole.customer];
      default:
        return [];
    }
  };

  const isBusy = formContext.status === ContextStatus.LOADING;

  return (
    <FormCard
      title="Formulaire de création"
      showTitle={false}
      onCreated={() => formContext.submit()}
      isCreateLoading={formContext.status === ContextStatus.LOADING}
    >
      <Form layout="vertical">
        <Form.Item label="Type de compte">
          <Select
            value={formContext.user.type}
            onChange={formContext.onUserTypeChange}
            disabled={isBusy}
          >
            {Object.keys(UserType).map((key) => (
              <Option
                key={key}
                value={UserType[key as keyof typeof UserType]}
                disabled={
                  !UserType[key as keyof typeof UserType].includes(
                    typeCreation || ''
                  )
                }
              >
                {userTypeAsString(UserType[key as keyof typeof UserType])}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <FormSectionTitle title="Informations de l'utilisateur" />
        <FormContainer>
          <Form.Item label="Avatar">
            <AvatarInput
              disabled={isBusy}
              onChange={(base64) => {
                formContext.onAvatarChange(base64);
              }}
            />
          </Form.Item>
          {formContext.user.type.includes(UserType.USER) ? (
            <>
              <Form.Item label="Prénom">
                <Input
                  placeholder="Ex: Jean"
                  value={formContext.user.firstname}
                  onChange={(e) =>
                    formContext.onUserFirstnameChange(e.target.value)
                  }
                  disabled={isBusy}
                />
              </Form.Item>

              <Form.Item label="Nom">
                <Input
                  placeholder="Ex: Dupont"
                  value={formContext.user.lastname}
                  onChange={(e) =>
                    formContext.onUserLastnameChange(e.target.value)
                  }
                  disabled={isBusy}
                />
              </Form.Item>
            </>
          ) : (
            <Form.Item label="Nom commercial"
              validateStatus={
                handleInputError(formContext.subscription.name, formContext.status)
              }
              help={
                handleErrorInputMessage(formContext.subscription.name, formContext.status)
              }>
              <Input
                placeholder="Ex: Agence immobilière de Paris"
                value={formContext.subscription.name}
                onChange={(e) => {
                  formContext.onSubscriptionNameChange(e.target.value);
                }}
                disabled={isBusy}
              />
            </Form.Item>
          )}

          <Form.Item
            label={'Téléphone'}
            validateStatus={
              handleInputError(formContext.user.phone, formContext.status)
            }
            help={
              handleErrorInputMessage(formContext.user.phone, formContext.status)
            }
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder={`Numéro (${formContext.selectedCountry.digitsLength} chiffres)`}
                value={formContext.phoneNumber}
                onChange={(e) => {
                  const digitsOnly = cleanPhoneNumber(e.target.value);
                  formContext.onUserPhoneNumberChange(digitsOnly);
                }}
                disabled={isBusy}
                maxLength={formContext.selectedCountry.digitsLength}
                allowClear
                style={{
                  width: '100%'
                }}
                addonBefore={
                  <Select
                    value={formContext.selectedCountry.name}
                    style={{ width: 100 }}
                    onChange={(value: string) => {
                      const selectedCountry = countryCodes.find(country => country.name === value);
                      if (selectedCountry) {
                        formContext.onUserSelectedCountryCodeChange(selectedCountry);
                      }
                    }}
                    popupMatchSelectWidth={false}
                    disabled={isBusy}
                    showSearch
                    filterOption={(input, option) => {
                      const childText = option?.children?.toString() || '';
                      return childText.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                  >
                    {countryCodes.map((country) => (
                      <Option key={country.name} value={country.name}>
                        {country.flag} {country.shortName || country.name.substring(0, 3)}
                      </Option>
                    ))}
                  </Select>
                }
                status={
                  formContext.phoneNumber && !isValidPhoneNumber(formContext.phoneNumber, formContext.selectedCountry.digitsLength)
                    ? 'error'
                    : undefined
                }
                suffix={
                  formContext.phoneNumber && isValidPhoneNumber(formContext.phoneNumber, formContext.selectedCountry.digitsLength)
                    ? <CheckOutlined style={{ color: 'green' }} />
                    : undefined
                }
                showCount
              />
            </div>
          </Form.Item>
          <Form.Item
            label="Email"
            validateStatus={
              handleInputError(formContext.user.email, formContext.status)
            }
            help={
              handleErrorInputMessage(formContext.user.email, formContext.status)
            }>
            <Input
              placeholder="Ex: moi@email.com"
              value={formContext.user.email}
              onChange={(e) => {
                // Change to lower case
                e.target.value = e.target.value.toLowerCase();

                // Remove spaces
                e.target.value = e.target.value.replace(/\s/g, '');
                formContext.onUserEmailChange(e.target.value);
              }}
              disabled={isBusy}
            />
          </Form.Item>
          <Form.Item
            label="Email d'authentification">
            <Input
              placeholder="Ex: moi@email.com"
              value={formContext.user.emailAuthentication}
              onChange={(e) => {
                // Change to lower case
                e.target.value = e.target.value.toLowerCase();

                // Remove spaces
                e.target.value = e.target.value.replace(/\s/g, '');
                formContext.onUserEmailAuthenticationChange(e.target.value);
              }}
              disabled={isBusy}
              suffix={
                <Popover
                  content={
                    <div style={{ width: '325px' }}>
                      <p>Ce champ est optionnel, il permet de créer un 'email d'alias' dans le cas ou l'email principal serait déjà utilisé. Cet email d'authentification sera l'email qui sera utiliser pour se connecter à la plateforme mais ensuite aucun autre email ne sera envoyé là dessus (notifications, news, etc..). L'email d'authentification permet donc de créer plusieurs compte avec la même adresse email de contact.</p>
                    </div>
                  }>
                  <QuestionCircleOutlined />
                </Popover>
              }
            />
          </Form.Item>

          <Form.Item
            label={
              <>
                Mot de passe &nbsp;&nbsp;
                <a onClick={generatePassword}>Générer <FieldTimeOutlined /></a>
              </>
            }
            validateStatus={
              handleInputError(formContext.user.password, formContext.status)
            }
            help={
              handleErrorInputMessage(formContext.user.password, formContext.status)
            }
          >
            <Input.Password
              placeholder="10 caractères : lettres minuscules & majuscules, chiffres, caractères spéciaux sauf caractères similaires)"
              value={formContext.user.password}
              onChange={(e) => {
                // Remove spaces
                e.target.value = e.target.value.replace(/\s/g, '');
                formContext.onUserPasswordChange(e.target.value);
              }}
              disabled={isBusy}
              visibilityToggle
              showCount
            />
          </Form.Item>

          <Form.Item label="Rôle">
            <Select
              value={formContext.user.role}
              onChange={formContext.onUserRoleChange}
              disabled={
                isBusy ||
                (authContext.authentication?.role !== UserRole.owner &&
                  authContext.authentication?.role !== UserRole.admin)
              }
            >
              {getAvailableRoles().map((key) => (
                <Option
                  key={key}
                  value={UserRole[key as keyof typeof UserRole]}
                >
                  {userRoleAsString(key)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea
              placeholder="Ex: Notes..."
              value={formContext.user.notes}
              onChange={(e) => {
                formContext.onUserNotesChange(e.target.value);
              }}
              disabled={isBusy}
            />
          </Form.Item>
        </FormContainer>
        {formContext.user.type === UserType.SUBSCRIPTION && (
          <>
            <FormSectionTitle title="Paramètres" />
            <FormContainer>
              <Form.Item label={<Flex align="center"><GithubOutlined style={{ marginRight: 6 }} />Compte bêta</Flex>}>
                <Switch
                  disabled={isBusy}
                  checked={formContext.subscription.settings?.beta_account}
                  onChange={(checked) => {
                    formContext.onSubscriptionSettingsBetaAccountChange(checked);
                  }}
                />
              </Form.Item>
              <Form.Item label={<Flex align="center"><VideoCameraAddOutlined style={{ marginRight: 6 }} />Compte de démonstration</Flex>}>
                <Switch
                  disabled={isBusy}
                  checked={formContext.subscription.settings?.demo_account}
                  onChange={(checked) => {
                    formContext.onSubscriptionSettingsDemoAccountChange(checked);
                  }}
                />
              </Form.Item>
              <Form.Item label={<Flex align="center"><CreditCardOutlined style={{ marginRight: 6 }} />Payg</Flex>}>
                <Switch
                  disabled={isBusy}
                  checked={formContext.subscription.settings?.payg_enabled}
                  onChange={(checked) => {
                    formContext.onSubscriptionSettingsPaygEnabledChange(checked);
                  }}
                />
              </Form.Item>
            </FormContainer>

            <FormContainer twoColumnsMax>
              <Form.Item label={<Flex align="center"><MailOutlined style={{ marginRight: 6 }} />Email en copie des notifications</Flex>}>
                <EmailList
                  values={formContext.subscription.settings?.email_notification_copy || []}
                  onChange={formContext.onSubscriptionSettingsEmailNotificationCopyChange}
                  placeholder="Ex: nom.prenom@domaine.com"
                  pageSize={5}
                />
              </Form.Item>
            </FormContainer>
            <FormSectionTitle title="Abonnement" />
            <FormContainer twoColumnsMax>
              <Form.Item label="Employé en charge du client">
                <Select
                  showSearch
                  placeholder="Sélectionner une personne..."
                  value={formContext.subscription.employeeInChargeUid}
                  onChange={(value) => {
                    formContext.onEmployeeInChargeChange(value)
                  }}
                >
                  {
                    usersContext.items.map((user) => (
                      <Option key={user.uid} value={user.uid}>
                        {user.firstname + ' ' + user.lastname}
                      </Option>
                    ))
                  }
                </Select>
              </Form.Item>

              <Form.Item label="Source des données (CRM)">
                <Select
                  showSearch
                  placeholder="Sélectionner une source de données..."
                  value={formContext.subscription.datasource}
                  onChange={(value) => {
                    formContext.onSubscriptionDataSourceChange(value as SubscriptionDataSource)
                  }}
                >
                  {
                    Object.values(SubscriptionDataSource).map((datasource) => (
                      <Option key={datasource} value={datasource}>
                        {subscriptionDataSourceAsString(datasource)}
                      </Option>
                    ))
                  }
                </Select>
              </Form.Item>
            </FormContainer>
            <FormContainer twoColumnsMax>
              <Form.Item label="Plan">
                <Select
                  value={formContext.subscription.idPlan}
                  onChange={(value) => {
                    formContext.onSubscriptionIdPlanChange(value);
                  }}
                  disabled={isBusy}
                >
                  {plansContext.items.filter((plan) => !plan.archived).map((plan) => (
                    <Option key={plan.id} value={plan.id}>
                      {plan.name}
                    </Option>
                  ))}
                </Select>
                {formContext.subscription.idPlan === 'c' && (
                  <Flex vertical gap={8} style={{ marginTop: '8px' }}>
                    <Flex gap={8} align="center">
                      <span title={creditTypeAsString(ECreditType.sms, false)}>{creditTypeAsString(ECreditType.sms)}</span>
                      <Input title="SMS"
                        type="number"
                        min={0}
                        max={10000000}
                        placeholder="1000"
                        value={formContext.subscription?.credits.sms}
                        onChange={(e) => {
                          formContext.onSubscriptionCreditsChange(
                            ECreditType.sms,
                            parseInt(e.target.value)
                          );
                        }}
                        disabled={isBusy}
                      />
                    </Flex>

                    <Flex gap={8} align="center">
                      <span title={creditTypeAsString(ECreditType.minutes, false)}>{creditTypeAsString(ECreditType.minutes)}</span>
                      <Input title="Minutes"
                        type="number"
                        min={0}
                        max={10000000}
                        placeholder="1000"
                        value={formContext.subscription?.credits.minutes}
                        onChange={(e) => {
                          formContext.onSubscriptionCreditsChange(
                            ECreditType.minutes,
                            parseInt(e.target.value)
                          );
                        }}
                        disabled={isBusy}
                      />
                    </Flex>

                    <Flex gap={8} align="center">
                      <span title={creditTypeAsString(ECreditType.contacts, false)}>{creditTypeAsString(ECreditType.contacts)}</span>
                      <Input title="Contacts"
                        type="number"
                        min={0}
                        max={10000000}
                        placeholder="1000"
                        value={formContext.subscription?.credits.contacts}
                        onChange={(e) => {
                          formContext.onSubscriptionCreditsChange(
                            ECreditType.contacts,
                            parseInt(e.target.value)
                          );
                        }}
                        disabled={isBusy}
                      />
                    </Flex>
                  </Flex>
                )}
              </Form.Item>
              <Form.Item label={<>N° d'envoyeur</>}>
                <Select
                  value={formContext.subscription.sender}
                  onChange={(value) => {
                    formContext.onSubscriptionSenderChange(value);
                  }}
                  disabled={isBusy || numbersContext?.items.length === 0}
                >
                  <Option key={""} value={""}>
                    SANS NUMERO (attribution ultérieure)
                  </Option>
                  {numbersContext.items.filter(item => item.available === true).map((number) => (
                    <Option key={number.sender} value={number.sender}>
                      {number.group_name + ' - ' + number.country.charAt(0).toUpperCase() + number.country.slice(1) + ' (' + number.channel + ') - ' + number.provider}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </FormContainer>
            <FormContainer twoColumnsMax>
              <Form.Item label="Début d'abonnement">
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={isBusy}
                  format={'DD/MM/YYYY'}
                  onChange={(date) =>
                    formContext.onSubscriptionPlanStartChange(date.toDate())
                  }
                  value={dayjs(formContext.subscription.planStart)}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item label="Fin d'abonnement">
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={isBusy}
                  format={'DD/MM/YYYY'}
                  onChange={(date) =>
                    formContext.onSubscriptionPlanEndChange(date.toDate())
                  }
                  value={dayjs(formContext.subscription.planEnd)}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item label="Ecran de paiement">
                <Select
                  placeholder="Non"
                  disabled={isBusy}
                  onChange={(value) => {
                    formContext.onSubscriptionSettingsPaymentWallChange(
                      value === 'true'
                    );
                  }}
                  value={
                    formContext.subscription.settings?.payment_wall !== undefined
                      ? formContext.subscription.settings.payment_wall.toString()
                      : 'false'
                  }
                >
                  <Option value="true"><CheckCircleFilled style={{ color: 'green', marginRight: '8px' }} />Oui</Option>
                  <Option value="false"><CloseCircleFilled style={{ color: 'red', marginRight: '8px' }} />Non</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Notes">
                <Input.TextArea
                  value={formContext.subscription.notes}
                  onChange={(e) => formContext.onSubscriptionNotesChange(e.target.value)}
                  placeholder="Notes..."
                  rows={3}
                />
              </Form.Item>
            </FormContainer>
            <FormContainer>
              <Form.Item label="Ville du fuseau horaire">
                <Select
                  disabled={isBusy}
                  onChange={(value) =>
                    formContext.onSubscriptionTimezoneCityChange(
                      value as SubscriptionTimezoneCity
                    )
                  }
                  value={formContext.subscription.timezone.city}
                  options={Object.keys(SubscriptionTimezoneCity).map((key) => (
                    {
                      label: subscriptionTimezoneCityAsString(SubscriptionTimezoneCity[key as keyof typeof SubscriptionTimezoneCity]),
                      value: SubscriptionTimezoneCity[key as keyof typeof SubscriptionTimezoneCity],
                    }
                  ))}
                />
              </Form.Item>
              <Form.Item label="Pays du fuseau horaire">
                <Select
                  disabled={isBusy}
                  onChange={(value) =>
                    formContext.onSubscriptionTimezoneCountryChange(
                      value as SubscriptionTimezoneCountry
                    )
                  }
                  value={formContext.subscription.timezone.country}
                  options={Object.keys(SubscriptionTimezoneCountry).map((key) => (
                    {
                      label: subscriptionTimezoneCountryAsString(SubscriptionTimezoneCountry[key as keyof typeof SubscriptionTimezoneCountry]),
                      value: SubscriptionTimezoneCountry[key as keyof typeof SubscriptionTimezoneCountry],
                    }
                  ))}
                />
              </Form.Item>
              <Form.Item label="Continent du fuseau horaire">
                <Select
                  disabled={isBusy}
                  onChange={(value) =>
                    formContext.onSubscriptionTimezoneContinentChange(
                      value as SubscriptionTimezoneContinent
                    )
                  }
                  value={formContext.subscription.timezone.continent}
                  options={Object.keys(SubscriptionTimezoneContinent).map((key) => (
                    {
                      label: subscriptionTimezoneContinentAsString(SubscriptionTimezoneContinent[key as keyof typeof SubscriptionTimezoneContinent]),
                      value: SubscriptionTimezoneContinent[key as keyof typeof SubscriptionTimezoneContinent],
                    }
                  ))}
                />
              </Form.Item>
            </FormContainer>

          </>
        )}
        {formContext.user.type === UserType.SUBSCRIPTION && (
          <>
            <FormSectionTitle title="Intelligence Artificielle" />
            <FormContainer>
              <Form.Item
                label="Prénom"
                validateStatus={
                  handleInputError(formContext.ai.fullname, formContext.status)
                }
                help={
                  handleErrorInputMessage(formContext.ai.fullname, formContext.status)
                }>
                <Input
                  placeholder="Ex: Donna"
                  disabled={isBusy}
                  onChange={(e) => {
                    formContext.onAIFullnameChange(e.target.value);
                  }}
                  value={formContext.ai.fullname}
                />
              </Form.Item>
              <Form.Item label="Genre">
                <Select
                  disabled={isBusy}
                  onChange={(value) => {
                    formContext.onAIGenderChange(value as AIGender);
                  }}
                  value={formContext.ai.gender}
                >
                  {Object.keys(AIGender).map((key) => (
                    <Select.Option
                      key={key}
                      value={AIGender[key as keyof typeof AIGender]}
                    >
                      {aiGenderAsString(AIGender[key as keyof typeof AIGender])}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Fonction"
                validateStatus={
                  handleInputError(formContext.ai.profile, formContext.status)
                }
                help={
                  handleErrorInputMessage(formContext.ai.profile, formContext.status)
                }>
                <Input
                  placeholder="Ex: Assistante de l'agent immobilier"
                  disabled={isBusy}
                  onChange={(e) => {
                    formContext.onAIProfileChange(e.target.value);
                  }}
                  value={formContext.ai.profile}
                />
              </Form.Item>

              <Form.Item
                label={
                  <>
                    Domaine
                    <Button
                      type='link'
                      disabled={domainsContext.status === ContextStatus.LOADING}
                      style={{
                        color: 'black',
                      }}
                      icon={domainsContext.status === ContextStatus.LOADING ? <LoadingOutlined /> : <ReloadOutlined />}
                      onClick={() => {
                        domainsContext.fetch(true);
                      }} />
                  </>
                }
                validateStatus={
                  handleInputError(formContext.ai.domain, formContext.status)
                }
                help={
                  handleErrorInputMessage(formContext.ai.domain, formContext.status)
                }>
                <DomainsSelect
                  disabled={isBusy}
                  onChange={(value) => {
                    formContext.onAIDomainChange(value as string);
                  }}
                  value={formContext.ai.domain}
                />
              </Form.Item>
              <Form.Item
                label="Localisation"
                validateStatus={
                  handleInputError(formContext.ai.location, formContext.status)
                }
                help={
                  handleErrorInputMessage(formContext.ai.location, formContext.status)
                }>
                <Input
                  placeholder="Ex: Paris, France"
                  disabled={isBusy}
                  onChange={(e) => {
                    formContext.onAILocationChange(e.target.value);
                  }}
                  value={formContext.ai.location}
                />
              </Form.Item>
              <Form.Item label="Site web">
                <Input
                  placeholder="Ex: https://www.exemple.com"
                  disabled={isBusy}
                  onChange={(e) => {
                    // Remove spaces
                    e.target.value = e.target.value.replace(/\s/g, '');
                    formContext.onAIWebsiteChange(e.target.value);
                  }}
                  value={formContext.ai.website}
                />
              </Form.Item>
              <Form.Item label="Type de langage">
                <Select
                  disabled={isBusy}
                  onChange={(value) => {
                    formContext.onAITonChange(value as AITon);
                  }}
                  value={formContext.ai.ton}
                >
                  {Object.keys(AITon).map((key) => (
                    <Select.Option
                      key={key}
                      value={AITon[key as keyof typeof AITon]}
                    >
                      {aiTonAsString(AITon[key as keyof typeof AITon])}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </FormContainer>
            <FormContainer twoColumnsMax>
              <Form.Item
                label="Intro">
                <TextArea
                  rows={8}
                  minLength={500}
                  maxLength={10000}
                  showCount
                  placeholder="Une description longue de la structure et de ses services. (500-1200 caractères recommandés)"
                  disabled={isBusy}
                  onChange={(e) => {
                    formContext.onAIIntroChange(e.target.value);
                  }}
                  value={formContext.ai.intro}
                />
              </Form.Item>
            </FormContainer>
            <FormContainer twoColumnsMax>
              <Form.Item
                label="Délai avant envoi d'une 1ère relance"
                style={{ flex: 1 }}
              >
                <Select
                  onChange={(value) =>
                    formContext.onAIFirstReminderTimingChange(value)
                  }
                  value={formContext.ai.reminderTiming[0]}
                >
                  {Object.keys(EAIReminderTiming).map((key) => (
                    <Select.Option
                      key={key}
                      value={Number(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    >
                      {aiReminderTimingAsString(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Délai avant envoi d'une 2ème relance (relance exécutée X heures / jours après la 1ère)"
                style={{ flex: 1 }}
              >
                <Select
                  onChange={(value) =>
                    formContext.onAISecondReminderTimingChange(value)
                  }
                  value={formContext.ai.reminderTiming[1]}
                >
                  {Object.keys(EAIReminderTiming).map((key) => (
                    <Select.Option
                      key={key}
                      value={Number(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    >
                      {aiReminderTimingAsString(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </FormContainer>
          </>
        )}
      </Form>
    </FormCard>
  );
};

export default UserCreationForm;
